<template>
  <nav class="pagination-container" aria-label="Навигация по страницам" @keydown="onKeydown">
    <ul class="pagination">
      <li>
        <button class="nav-button" type="button" :disabled="isFirstPage" aria-label="Перейти на первую страницу" title="В начало" @click="changePage(1)">
          <span aria-hidden="true">«</span>
        </button>
      </li>
      <li>
        <button class="nav-button" type="button" :disabled="isFirstPage" aria-label="Перейти на предыдущую страницу" title="Назад" @click="changePage(activePage - 1)">
          <span aria-hidden="true">‹</span>
        </button>
      </li>
      <li v-for="(item, index) in items" :key="`${item.type}-${item.page}-${index}`" class="page-item">
        <button
          v-if="item.type === 'page'"
          class="page-button"
          type="button"
          :class="{ active: item.page === activePage }"
          :aria-current="item.page === activePage ? 'page' : undefined"
          :aria-label="item.page === activePage ? `Текущая страница, ${item.page}` : `Перейти на страницу ${item.page}`"
          @click="changePage(item.page)"
        >
          {{ item.page }}
        </button>
        <button
          v-else
          class="ellipsis-button"
          type="button"
          :aria-label="`Перейти на страницу ${item.page}`"
          :title="item.direction === 'backward' ? 'На несколько страниц назад' : 'На несколько страниц вперёд'"
          @click="changePage(item.page)"
        >
          <span aria-hidden="true">…</span>
        </button>
      </li>
      <li>
        <button class="nav-button" type="button" :disabled="isLastPage" aria-label="Перейти на следующую страницу" title="Вперёд" @click="changePage(activePage + 1)">
          <span aria-hidden="true">›</span>
        </button>
      </li>
      <li>
        <button class="nav-button" type="button" :disabled="isLastPage" aria-label="Перейти на последнюю страницу" title="В конец" @click="changePage(total)">
          <span aria-hidden="true">»</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<script>
import { computed } from 'vue'
import { normalizeCurrentPage, normalizeTotalPages, paginationItems } from './paginationState.mjs'

export default {
  name: 'my-pagination',
  props: {
    totalPages: { type: [Number, String], default: 1 },
    currentPage: { type: [Number, String], default: 1 },
  },
  emits: ['pageChanged'],
  setup(props, { emit }) {
    const total = computed(() => normalizeTotalPages(props.totalPages))
    const activePage = computed(() => normalizeCurrentPage(props.currentPage, total.value))
    const items = computed(() => paginationItems(total.value, activePage.value))
    const isFirstPage = computed(() => activePage.value === 1)
    const isLastPage = computed(() => activePage.value === total.value)

    function changePage(page) {
      const next = normalizeCurrentPage(page, total.value)
      if (next !== activePage.value) emit('pageChanged', next)
    }

    function onKeydown(event) {
      const targetPage = {
        ArrowLeft: activePage.value - 1,
        ArrowRight: activePage.value + 1,
        Home: 1,
        End: total.value,
      }[event.key]
      if (targetPage === undefined) return
      event.preventDefault()
      changePage(targetPage)
    }

    return { total, activePage, items, isFirstPage, isLastPage, changePage, onKeydown }
  },
}
</script>

<style scoped>
.pagination-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  color: #475467;
  user-select: none;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.pagination button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 36px;
  height: 36px;
  padding: 0 9px;
  box-sizing: border-box;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #344054;
  font: inherit;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  transition: border-color 120ms ease, background-color 120ms ease, color 120ms ease;
  user-select: none;
}

.pagination button:hover:not(:disabled):not(.active) {
  border-color: #84adff;
  background: #f0f6ff;
  color: #0056b3;
}

.pagination button:focus-visible {
  outline: 3px solid rgba(0, 123, 255, .3);
  outline-offset: 1px;
}

.pagination button.active {
  border-color: #007bff;
  background: #007bff;
  color: #fff;
  font-weight: 700;
}

.pagination button:disabled {
  border-color: #eaecf0;
  background: #f8fafc;
  color: #98a2b3;
  cursor: not-allowed;
}

.nav-button { padding: 0 !important; font-size: 18px; }
.ellipsis-button { font-weight: 700; }

@media (max-width: 769px) {
  .pagination-container { width: 100%; }
  .pagination { max-width: 100%; overflow-x: auto; padding: 2px; }
}
</style>
